/**
 * Popper Component
 *
 * The following code is a derivative of the amazing work done by the Material UI team.
 * Original source: https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Popper/Popper.js
 */

import cx from 'classnames2';
import PopperJS, { PopperOptions } from 'popper.js';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { IPortalProps, Portal } from '../Portal';
import { createChainedFunction, setRef, useEnhancedEffect, useForkRef } from '../utils';

import styles from './styles.module.scss';

/**
 * Flips placement if in <body dir="rtl" />
 */
function flipPlacement(placement) {
  // tslint:disable-next-line:no-typeof-undefined
  const direction = (typeof window !== 'undefined' && document.body.getAttribute('dir')) || 'ltr';

  if (direction !== 'rtl') {
    return placement;
  }

  switch (placement) {
    case 'bottom-end':
      return 'bottom-start';
    case 'bottom-start':
      return 'bottom-end';
    case 'top-end':
      return 'top-start';
    case 'top-start':
      return 'top-end';
    default:
      return placement;
  }
}

function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}

export interface IPopperProps {
  ref?: React.Ref<HTMLElement>;
  id?: string;
  tabIndex?: number;
  className?: string;
  children?: any;
  role?: string;
  dataTest?: string;
  style?: React.CSSProperties;
  /**
   * The reference element, or a function that returns the reference element,
   * that will be used to set the placment of the popover.
   *
   * The reference element should be an HTML Element instance or a referenceObject:
   * https://popper.js.org/popper-documentation.html#referenceObject.
   */
  anchorEl?: null | PopperJS.ReferenceObject | (() => PopperJS.ReferenceObject);
  /**
   * The container where the Popper should render.
   * By default, the portal renders it's children as a child of `document.body`
   */
  container?: IPortalProps['container'];
  /**
   * If `true`, the Popper will display in a portal.
   * [Learn more about portals](https://reactjs.org/docs/portals.html)
   *
   */
  usePortal?: boolean;
  /**
   * If `true`, the component will unmount when it closes
   *
   */
  unmountOnExit?: boolean;
  /**
   * This is used to configure how Popper.js computes the positioning of the popper.
   *
   */
  modifiers?: PopperJS.Modifiers;
  /**
   * If `true`, the popper will be opened in controlled mode
   */
  isOpen?: boolean;
  /**
   * The placement of the popper
   */
  placement?: PopperJS.Placement;
  /**
   * Options provided to the [`popper.js`](https://github.com/FezVrasta/popper.js) instance.
   */
  popperOptions?: PopperOptions;
  /**
   * A ref that points to the used popper instance.
   */
  popperRef?: React.RefObject<PopperJS>;

  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
}

const PopperComp: React.FC<IPopperProps> = (
  {
    className,
    anchorEl,
    children,
    container,
    usePortal = true,
    unmountOnExit = true,
    modifiers,
    isOpen,
    placement: initialPlacement = 'bottom',
    popperOptions = {},
    popperRef: popperRefProp,
    dataTest,
    style,
    ...rest
  },
  ref
) => {
  const tooltipRef = useRef(null);
  const ownRef = useForkRef(tooltipRef, ref);

  const popperRef = useRef(null);
  const handlePopperRef = useForkRef(popperRef, popperRefProp);
  const handlePopperRefRef = useRef(handlePopperRef);

  useEnhancedEffect(() => {
    handlePopperRefRef.current = handlePopperRef;
  }, [handlePopperRef]);

  useImperativeHandle(popperRefProp, () => popperRef.current, []);

  const rtlPlacement = flipPlacement(initialPlacement);

  const [placement, setPlacement] = useState(rtlPlacement);
  if (rtlPlacement !== placement) {
    setPlacement(rtlPlacement);
  }

  const handleOpen = useCallback(() => {
    const popperNode = tooltipRef.current;

    if (!popperNode || !anchorEl || !isOpen) {
      return;
    }

    if (popperRef.current) {
      popperRef.current.destroy();
      handlePopperRefRef.current(null);
    }

    const handlePopperUpdate = (data) => {
      setPlacement(data.placement);
    };

    const popper = new PopperJS(getAnchorEl(anchorEl), popperNode, {
      placement: rtlPlacement,
      ...popperOptions,
      modifiers: {
        ...(usePortal && {
          preventOverflow: {
            boundariesElement: 'window'
          }
        }),
        ...modifiers,
        ...popperOptions.modifiers
      },
      onUpdate: createChainedFunction(handlePopperUpdate, popperOptions.onUpdate)
    });
    handlePopperRefRef.current(popper);
  }, [anchorEl, usePortal, modifiers, isOpen, rtlPlacement, popperOptions]);

  const handleRef = useCallback(
    (node) => {
      setRef(ownRef, node);
      handleOpen();
    },
    [ownRef, handleOpen]
  );

  const handleClose = () => {
    if (!popperRef.current) {
      return;
    }

    popperRef.current.destroy();
    handlePopperRefRef.current(null);
  };

  useEffect(() => {
    handleOpen();
  }, [handleOpen]);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen]);

  if (unmountOnExit && !isOpen) {
    return null;
  }

  const childProps = { placement, transition: null };

  return (
    <Portal isDisabled={!usePortal} container={container}>
      <div ref={handleRef as any} className={cx(styles.popper, className)} style={style} data-test={dataTest} {...rest}>
        {typeof children === 'function' ? children(childProps) : children}
      </div>
    </Portal>
  );
};

// Please delete once Voltron and Zeus are on the same version of typescript and use
// export const Popper = forwardRef((props, ref) => ...
const PopperRefWrap = () => forwardRef(PopperComp);
export const Popper = PopperRefWrap();
